import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Building2, MapPin, Phone, Mail, ExternalLink, Loader2, Save, Globe } from "lucide-react";
import { API } from "../App";

const isValidZip = (z) => /^\d{5}$/.test((z || "").trim());

/**
 * Displays the user's local Workforce Development Board (via CareerOneStop API)
 * based on the `home_zip` saved on their profile. Users can edit the zip inline.
 *
 * Required by CareerOneStop terms of service: show their attribution + link.
 */
const WorkforceBoardCard = ({ user, onZipSaved }) => {
  const [zip, setZip] = useState(user?.home_zip || "");
  const [editing, setEditing] = useState(!user?.home_zip);
  const [savingZip, setSavingZip] = useState(false);
  const [boards, setBoards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setZip(user?.home_zip || "");
    setEditing(!user?.home_zip);
  }, [user?.home_zip]);

  const fetchBoards = useCallback(async (z) => {
    if (!isValidZip(z)) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const r = await fetch(`${API}/public/workforce-board?zip=${z}&radius=25&lid=2`, { credentials: "include" });
      if (!r.ok) {
        if (r.status === 503) setErrorMsg("Workforce board lookup is not configured. Contact your admin.");
        else setErrorMsg("Couldn't reach the workforce board service.");
        setBoards([]);
        return;
      }
      const d = await r.json();
      setBoards(d.boards || []);
      if ((d.boards || []).length === 0) setErrorMsg(d.error || "No local workforce boards found within 25 miles.");
    } catch (e) {
      setErrorMsg("Network error fetching workforce board.");
      setBoards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch when we have a saved zip
  useEffect(() => {
    if (user?.home_zip && isValidZip(user.home_zip)) fetchBoards(user.home_zip);
  }, [user?.home_zip, fetchBoards]);

  const saveZip = async () => {
    const z = (zip || "").trim();
    if (!isValidZip(z)) { toast.error("Enter a valid 5-digit US ZIP code"); return; }
    setSavingZip(true);
    try {
      const r = await fetch(`${API}/users/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ home_zip: z }),
      });
      if (!r.ok) throw new Error("Save failed");
      toast.success("ZIP saved");
      setEditing(false);
      onZipSaved?.();
      fetchBoards(z);
    } catch (e) {
      toast.error("Couldn't save ZIP");
    } finally {
      setSavingZip(false);
    }
  };

  return (
    <Card className="card-surface" data-testid="workforce-board-card">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand-navy" />
          Your Local Workforce Board
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Your nearest American Job Center / Workforce Development Board — they administer WIOA funding,
          which can cover your Aspiritech tuition if you qualify.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[160px] max-w-[260px]">
            <Label className="text-slate-600 text-xs">Home ZIP Code (US)</Label>
            <Input
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="60606"
              maxLength={5}
              className="input-base mt-1 font-mono tracking-wider"
              data-testid="home-zip-input"
              disabled={!editing && !!user?.home_zip}
            />
          </div>
          {editing ? (
            <Button onClick={saveZip} disabled={savingZip || !isValidZip(zip)} className="btn-primary" data-testid="save-zip-btn">
              {savingZip ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving</> : <><Save className="w-4 h-4 mr-1.5" /> Save & Find</>}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-zip-btn">Change</Button>
          )}
        </div>

        {/* Results */}
        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Looking up boards near {zip}…
          </div>
        )}

        {!loading && boards !== null && boards.length === 0 && errorMsg && (
          <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="workforce-board-empty">
            {errorMsg}
          </p>
        )}

        {!loading && boards && boards.length > 0 && (
          <ul className="space-y-3" data-testid="workforce-board-results">
            {boards.slice(0, 4).map((b) => (
              <li key={b.id} className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`board-${b.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">{b.name}</p>
                    {b.company && <p className="text-xs text-slate-500 mt-0.5">{b.company}</p>}
                    {(b.address1 || b.city) && (
                      <p className="text-xs text-slate-600 mt-2 flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                        <span>
                          {b.address1}
                          {b.address2 ? `, ${b.address2}` : ""}
                          {(b.city || b.state || b.zip) && <><br />{[b.city, b.state, b.zip].filter(Boolean).join(", ")}</>}
                        </span>
                      </p>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-bold ${
                    b.level === "LOCAL" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                  }`}>
                    {b.level === "LOCAL" ? "Local" : "State"}
                  </span>
                </div>

                {b.contacts && b.contacts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                    {b.contacts.slice(0, 2).map((c, i) => (
                      <div key={i} className="text-xs text-slate-600">
                        {c.name && <span className="font-medium text-slate-800">{c.name}</span>}
                        {c.title && <span className="text-slate-500"> · {c.title}</span>}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-brand-navy hover:underline"><Phone className="w-3 h-3" />{c.phone}</a>}
                          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-brand-navy hover:underline"><Mail className="w-3 h-3" />{c.email}</a>}
                          {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-navy hover:underline"><ExternalLink className="w-3 h-3" />Website</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
            {boards.length > 4 && (
              <p className="text-xs text-slate-500 text-center">+ {boards.length - 4} more nearby</p>
            )}
          </ul>
        )}

        {/* CareerOneStop attribution (required by their TOS) */}
        <p className="text-[10px] text-slate-400 flex items-center gap-1 pt-2 border-t border-slate-100">
          <Globe className="w-3 h-3" />
          Data provided by <a href="https://www.careeronestop.org" target="_blank" rel="noopener noreferrer" className="hover:text-brand-navy underline">CareerOneStop</a>,
          sponsored by the U.S. Department of Labor.
        </p>
      </CardContent>
    </Card>
  );
};

export default WorkforceBoardCard;
